import { Reference } from '@approvalmax/types';
import { mathService, numberHelpers } from '@approvalmax/utils';
import { useAtom } from 'jotai';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { NetSuiteItem } from 'pages/requestForm/data/netsuite/NetSuiteContext';
import { getNetSuiteContext } from 'pages/requestForm/selectors/netsuite/netSuiteSelectors';
import { useCallback, useEffect } from 'react';

import {
    calculateLineAmountsBasedOnAmount,
    reorderLines,
    setCurrencyPrecision,
    setItemLineCurrencyPrecision,
} from '../../helpers';
import { checkItemLineIsEmpty } from '../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteItemLinesState } from '../../resources/states';
import { useChangeNetSuiteRequest } from '../useChangeNetSuiteRequest/useChangeNetSuiteRequest';
import { useCurrencyPrecision } from '../useCurrencyPrecision/useCurrencyPrecision';
import { useGetAndIncreaseItemLineNumber } from '../useGetAndIncreaseItemLineNumber/useGetAndIncreaseItemLineNumber';
import { useUpdateLineCustomField } from '../useUpdateLineCustomField/useUpdateLineCustomField';
import { createNewItemLine, expandLineItem, generateItemLineId, getUpdatedItemLine } from './useItemLines.helpers';
import {
    CloneLineHandler,
    FieldUpdateHandler,
    MultiLineFieldUpdateHandler,
    RemoveLineHandler,
    ReorderHandler,
    UpdateItemsOnCurrencyChangeHandler,
    UpdateLineHandler,
} from './useItemLines.types';

export const useItemLines = (currency: Reference | null | undefined) => {
    const [itemLines, setItemLinesState] = useAtom(netSuiteItemLinesState);

    const getAndIncreaseItemLineNumber = useGetAndIncreaseItemLineNumber();

    const { currencies } = useSelector(getNetSuiteContext);

    const currencyPrecision = useCurrencyPrecision(currency);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    useEffect(() => {
        if (!itemLines.length) {
            const itemLineNumber = getAndIncreaseItemLineNumber();

            setItemLinesState([createNewItemLine(itemLineNumber)]);
        }
    }, [itemLines, setItemLinesState, getAndIncreaseItemLineNumber]);

    const setItemLines = useCallback(
        (data: domain.NetSuiteItemLine[]) => {
            if (data.length === 0) {
                const itemLineNumber = getAndIncreaseItemLineNumber();

                setItemLinesState([createNewItemLine(itemLineNumber)]);

                return;
            }

            setItemLinesState(
                data.map((item) =>
                    setItemLineCurrencyPrecision(
                        expandLineItem({
                            ...item,
                            ...calculateLineAmountsBasedOnAmount(item.amount, item.taxRate, currencyPrecision),
                        }),
                        currencyPrecision
                    )
                )
            );
        },
        [currencyPrecision, setItemLinesState, getAndIncreaseItemLineNumber]
    );

    const onAddLine = useCallback(() => {
        const itemLineNumber = getAndIncreaseItemLineNumber();

        setItemLinesState((current) => [...current, createNewItemLine(itemLineNumber)]);

        changeNetSuiteRequest();
    }, [getAndIncreaseItemLineNumber, setItemLinesState, changeNetSuiteRequest]);

    const onReorder = useCallback<ReorderHandler>(
        (oldIndex, newIndex) => {
            setItemLinesState((current) => reorderLines(current, oldIndex, newIndex));

            changeNetSuiteRequest();
        },
        [setItemLinesState, changeNetSuiteRequest]
    );

    const onRemoveLine = useCallback<RemoveLineHandler>(
        (id) => {
            setItemLinesState((current) => current.filter((item) => item.id !== id));

            changeNetSuiteRequest();
        },
        [setItemLinesState, changeNetSuiteRequest]
    );

    const onCloneLine = useCallback<CloneLineHandler>(
        (id) => {
            const itemLineNumber = getAndIncreaseItemLineNumber();

            setItemLinesState((current) => {
                const targetItem = current.find((item) => item.id === id);

                if (targetItem) {
                    return [
                        ...current,
                        {
                            ...targetItem,
                            id: generateItemLineId(),
                            lineNumber: itemLineNumber,
                        },
                    ];
                }

                return current;
            });

            changeNetSuiteRequest();
        },
        [getAndIncreaseItemLineNumber, setItemLinesState, changeNetSuiteRequest]
    );

    const onItemChange = useCallback(
        (id: string, value: NetSuiteItem | null) => {
            setItemLinesState((current) =>
                current.map((line) => {
                    if (line.id !== id) {
                        return line;
                    }

                    if (!value) {
                        const newLine = {
                            ...line,
                            item: null,
                            vendorName: null,
                            units: null,
                            unitList: [],
                        };

                        newLine.isEmpty = checkItemLineIsEmpty(newLine);

                        return newLine;
                    }

                    const unitList = value.unitList || [];

                    const baseUnit = unitList.find((unit) => unit.isItemPrimaryPurchaseUnit);

                    const defaultRate = setCurrencyPrecision(
                        baseUnit ? baseUnit.catalogUnitPrice : value.rate,
                        currencyPrecision
                    );

                    const amount =
                        numberHelpers.isNumber(line.quantity) && numberHelpers.isNumber(value.rate)
                            ? setCurrencyPrecision(mathService.multiply(line.quantity, value.rate), currencyPrecision)
                            : null;

                    return {
                        ...line,
                        ...calculateLineAmountsBasedOnAmount(amount, line.taxRate, currencyPrecision),
                        item: value,
                        vendorName: value.vendorName,
                        description: value.purchaseDescription,
                        units: baseUnit || null,
                        unitList: value.unitList || [],
                        unitPrice: defaultRate,
                        defaultRate: defaultRate,
                        isEmpty: false,
                    };
                })
            );

            changeNetSuiteRequest();
        },
        [currencyPrecision, setItemLinesState, changeNetSuiteRequest]
    );

    const onFieldUpdate = useCallback<FieldUpdateHandler>(
        (id, key, value) => {
            setItemLinesState((current) =>
                current.map((item) => {
                    if (item.id === id) {
                        return getUpdatedItemLine(item, key, value, currencyPrecision);
                    }

                    return item;
                })
            );

            changeNetSuiteRequest();
        },
        [currencyPrecision, setItemLinesState, changeNetSuiteRequest]
    );

    const onMultiLineFieldUpdate = useCallback<MultiLineFieldUpdateHandler>(
        (key, value) => {
            setItemLinesState((current) =>
                current.map((item) => getUpdatedItemLine(item, key, value, currencyPrecision))
            );

            changeNetSuiteRequest();
        },
        [currencyPrecision, setItemLinesState, changeNetSuiteRequest]
    );

    const updateLine = useCallback<UpdateLineHandler>(
        (id, data) => {
            setItemLinesState((current) =>
                current.map((item) => {
                    if (item.id === id) {
                        const updatedItem = {
                            ...item,
                            ...data,
                        };

                        updatedItem.isEmpty = checkItemLineIsEmpty(updatedItem);

                        return updatedItem;
                    }

                    return item;
                })
            );

            changeNetSuiteRequest();
        },
        [setItemLinesState, changeNetSuiteRequest]
    );

    const updateItemsOnCurrencyChange = useCallback<UpdateItemsOnCurrencyChangeHandler>(
        (newCurrency) => {
            setItemLinesState((current) => {
                return current.map((item) => {
                    if (!newCurrency) {
                        return item;
                    }

                    const newCurrencyPrecision = currencies.find(({ id }) => id === newCurrency?.id)?.precision ?? 2;

                    if (currencyPrecision !== newCurrencyPrecision) {
                        return setItemLineCurrencyPrecision(item, newCurrencyPrecision);
                    }

                    return item;
                });
            });

            changeNetSuiteRequest();
        },
        [currencies, currencyPrecision, setItemLinesState, changeNetSuiteRequest]
    );

    const updateCustomField = useUpdateLineCustomField(setItemLinesState);

    return {
        itemLines,
        setItemLines,
        onAddLine,
        onReorder,
        onRemoveLine,
        onCloneLine,
        onFieldUpdate,
        onMultiLineFieldUpdate,
        updateCustomField,
        updateLine,
        onItemChange,
        updateItemsOnCurrencyChange,
    };
};
